import React from "react";
import { useNavigate } from "react-router-dom";
import Icon, { type IconName } from "../../../components/AppIcon";

type LoginRole = "admin" | "tenant" | "technician" | "agent";

interface RoleContent {
	title: string;
	subtitle: string;
	icon: IconName;
	colorClass: string;
}

const ROLE_CONFIG: Record<LoginRole, RoleContent> = {
	admin: {
		title: "Administrator Login",
		subtitle: "Access your admin dashboard",
		icon: "Settings",
		colorClass: "bg-primary/10 text-primary",
	},
	tenant: {
		title: "Tenant Portal Login",
		subtitle: "Access your tenant dashboard",
		icon: "Home",
		colorClass: "bg-secondary/10 text-secondary",
	},
	technician: {
		title: "Technician Login",
		subtitle: "Access your work dashboard",
		icon: "Wrench",
		colorClass: "bg-accent/10 text-accent",
	},
	agent: {
		title: "Sales Agent Login",
		subtitle: "Access your sales dashboard",
		icon: "Users",
		colorClass: "bg-success/10 text-success",
	},
};

interface LoginHeaderProps {
	selectedRole: LoginRole | null;
}

const LoginHeader: React.FC<LoginHeaderProps> = ({ selectedRole }) => {
	const roleContent = selectedRole ? ROLE_CONFIG[selectedRole] : null;
	const navigate = useNavigate();
	return (
		<div className="mb-8 text-center">
			<div className="mb-6 flex items-center justify-center">
				<div
					className="flex items-center justify-center cursor-pointer"
					onClick={() => navigate("/")}
				>
					<img
						src="/assets/images/desktop_logo.png"
						alt="Dofarahub Logo"
						className="h-12 w-auto object-contain"
					/>
				</div>
			</div>

			<div className="mb-2">
				{roleContent ? (
					<>
						<div className="mb-3 flex items-center justify-center">
							<div
								className={`mr-2 flex h-8 w-8 items-center justify-center rounded-lg ${roleContent.colorClass}`}
							>
								<Icon name={roleContent.icon} size={16} />
							</div>
							<h2 className="text-xl font-semibold text-foreground">
								{roleContent.title}
							</h2>
						</div>
						<p className="text-sm text-muted-foreground">
							{roleContent.subtitle}
						</p>
					</>
				) : (
					<>
						<h2 className="mb-2 text-2xl font-semibold text-foreground">
							Welcome Back
						</h2>
						<p className="text-muted-foreground">
							Sign in to your Dofarahub account to manage your real estate
							operations
						</p>
					</>
				)}
			</div>

			{!selectedRole && (
				<div className="mt-6 rounded-lg bg-muted/50 p-4">
					<p className="mb-2 text-xs font-medium text-foreground">
						Quick Access for:
					</p>
					<div className="flex flex-wrap justify-center gap-2">
						<span className="rounded-full bg-primary/10 px-2 py-1 text-xs text-primary">
							Administrators
						</span>
						<span className="rounded-full bg-secondary/10 px-2 py-1 text-xs text-secondary">
							Tenants
						</span>
						<span className="rounded-full bg-accent/10 px-2 py-1 text-xs text-accent">
							Technicians
						</span>
						<span className="rounded-full bg-success/10 px-2 py-1 text-xs text-success">
							Agents
						</span>
					</div>
				</div>
			)}
		</div>
	);
};

export default LoginHeader;
